// Lo que los nodos y las barras necesitan del lienzo, sin prop drilling a través de
// React Flow. Vive en un módulo hoja a propósito: `LienzoDelAgente` importa los nodos y
// los nodos importan este contexto; si el contexto viviera en `LienzoDelAgente`, el
// ciclo duplicaría el `createContext` en desarrollo (ya pasó con `entornoContext.ts`).

import { createContext } from 'react';
import type React from 'react';
import type { useOpcionesDeDescarga } from '../../components/descarga/useOpcionesDeDescarga';
import type { AccionVisor } from '../../pages/GeneratedImagesHistory';
import type { VerificacionApi } from '../../pages/v2/verificacion/useVerificacion';
import type { ChatAttachment } from '../../pages/v2/agentAttachments';
import type { DatosCelda } from './acomodo';
import type { CeldaParaAccion } from './accionesDeCelda';
import type { Herramienta } from './herramientas/herramienta';
import type { Miniatura } from './useMiniaturasDelLienzo';

type Opciones = ReturnType<typeof useOpcionesDeDescarga>;

/** Lo que recibe una barra de acciones alternativa (la del modo Diseño). */
export interface PropsDeBarraDeCelda {
  id: string;
  datos: DatosCelda;
  /** La celda con su fila, si la tiene (una propia con `url` no). */
  celda: CeldaParaAccion | null;
  animada: boolean;
}

export interface ValorLienzo {
  companyId: string | null;
  brandId: string | null;
  tiendaConectada: boolean;
  miniaturas: Map<string, Miniatura>;
  /** Las celdas seleccionadas que ya tienen fila y miniatura (las accionables). */
  seleccionadas: CeldaParaAccion[];
  /**
   * Cuántas de las seleccionadas tienen barra — `seleccionadas.length` NO alcanza porque
   * ahí no entran las imágenes propias del modo Diseño (sin fila)
   * (ADR-2026-09-17-la-barra-cuenta-lo-accionable). Es lo que mira cada celda para saber
   * si es la única accionable y le toca mostrar su barra.
   */
  seleccionAccionable: number;
  /**
   * Con `origen` (el rectángulo de la foto en pantalla y su miniatura) quien recibe puede
   * animarla hacia donde va a quedar; el chat lo ignora.
   */
  agregarAdjunto: (a: ChatAttachment, origen?: { rect: DOMRect; src: string }) => void;
  /**
   * Cómo se llama el botón de agregar en las barras de la foto y de la selección. Por
   * defecto "Agregar al chat"; un modo que se queda con lo agregado (Redes lo pone de
   * referencia en su caja, ADR-2026-10-04-agregar-del-lienzo-va-a-la-caja-de-redes) lo
   * renombra.
   */
  rotuloAgregar?: string;
  /** Abre el visor prestado ("Ver"); con `accion`, dispara esa acción apenas abre. */
  abrir: (imageId: string, accion?: AccionVisor) => void;
  /** Abre el panel de una herramienta sobre esa celda, sin pasar por el visor
   *  (ADR-2026-09-16-las-herramientas-del-lienzo-abren-en-panel). */
  abrirHerramienta: (celda: CeldaParaAccion, herramienta: Herramienta) => void;
  avisarError: (msg: string) => void;
  /** La celda midió su imagen: con esto el acomodo usa la proporción REAL. */
  reportarRatio: (imageId: string, ratio: number) => void;
  resolverOpciones: Opciones['resolverOpciones'];
  aplicarOpciones: Opciones['aplicarOpciones'];
  /** Acomoda las celdas seleccionadas en una grilla, con animación
   *  (ADR-2026-09-17-la-seleccion-se-ordena-en-grilla). */
  ordenarSeleccion: () => void;
  /**
   * La verificación de calidad, cuando la empresa la tiene prendida
   * (ADR-2026-09-20-la-verificacion-de-calidad-llega-al-lienzo): cada foto dibuja la
   * misma capa que la columna de resultados (botón, velo de "analizando", pastilla con
   * el resultado y su popover). Sin ella, nada.
   */
  verificacion?: VerificacionApi;
  /**
   * Las ranuras que presta el lienzo (ADR-2026-09-17-el-lienzo-presta-tres-ranuras):
   * otra barra por celda, un adorno encima de cada foto y un interceptor del click.
   * Sin ellas, el lienzo del Agente: `BarraDeCelda`, nada, y el click abre el visor.
   */
  Barra?: React.ComponentType<PropsDeBarraDeCelda>;
  Adorno?: React.ComponentType<{ id: string; ratio: number }>;
  /** Devuelve `true` si se quedó con el click (no se abre el visor). */
  alTocarCelda?: (id: string) => boolean;
  /** Devuelve `true` si esa celda NO muestra su pie (el título) por ahora: el modo puso otra cosa debajo. */
  sinPie?: (id: string) => boolean;
  /**
   * La cuarta: una CAPA que se monta adentro de React Flow (con acceso a su store y a
   * `ViewportPortal`), para dibujar sobre el lienzo en sus coordenadas — las líneas de
   * una mezcla (ADR-2026-09-17-las-partes-se-atan-con-una-linea). Sin ella, nada.
   */
  Capa?: React.ComponentType;
}

export const LienzoContext = createContext<ValorLienzo>({
  companyId: null,
  brandId: null,
  tiendaConectada: false,
  miniaturas: new Map(),
  seleccionadas: [],
  seleccionAccionable: 0,
  agregarAdjunto: () => {},
  abrir: () => {},
  abrirHerramienta: () => {},
  avisarError: () => {},
  reportarRatio: () => {},
  resolverOpciones: async () => null,
  aplicarOpciones: async (b) => b,
  ordenarSeleccion: () => {},
});
